<template>
    <Popover class="relative" ref="reference">
        <div class="flex items-center gap-x-3">
            <PopoverButton
                v-if="$page.props.auth.user"
                :class="[
                    `bg-${rootModelClass}-accent text-${rootModelClass}`,
                    isCommentEmotions ? 'p-1' : 'pl-2 pr-2.5 py-1',
                    'focus-style rounded-full flex space-x-2 items-center',
                ]"
            >
                <PhSmiley class="size-6" weight="regular" />
                <p v-if="!isCommentEmotions" class="font-medium">
                    {{ $lang.get('Like') }}
                </p>
            </PopoverButton>
            <div class="flex items-center gap-x-2">
                <TransitionGroup
                    enter-active-class="transition-all duration-500"
                    move-class="transition-all duration-500"
                    enter-from-class="scale-0 opacity-0"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-[transform,opacity] duration-500 absolute"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-0"
                >
                    <div
                        v-for="emotion in activeEmotions"
                        :key="emotion.id"
                        class="cursor-help"
                    >
                        <TextTooltip
                            :text="getActiveEmotionTooltipText(emotion)"
                        >
                            <div
                                :class="
                                    emotion.user_interacted
                                        ? `bg-${rootModelClass}-accent text-${rootModelClass}`
                                        : ''
                                "
                                class="flex items-center gap-x-1.5 rounded-full px-2 py-0.5 border border-primary-border transition-colors duration-500 relative overflow-hidden"
                            >
                                <component
                                    :is="emotionIcons[emotion.id]"
                                    :class="
                                        isCommentEmotions ? 'size-4' : 'size-6'
                                    "
                                    :weight="
                                        emotion.user_interacted
                                            ? 'fill'
                                            : 'regular'
                                    "
                                />
                                <Transition
                                    :enter-from-class="
                                        emotion.count > emotion.previousCount
                                            ? '-translate-y-full'
                                            : 'translate-y-full'
                                    "
                                    :enter-to-class="'translate-y-0'"
                                    :leave-to-class="
                                        emotion.count > emotion.previousCount
                                            ? 'translate-y-full'
                                            : '-translate-y-full'
                                    "
                                    enter-active-class="transition-transform duration-300 ease-out z-10"
                                    leave-active-class="transition-transform duration-300 ease-out z-10"
                                    leave-from-class="translate-y-0"
                                    mode="out-in"
                                >
                                    <p
                                        :key="emotion.count"
                                        v-text="emotion.count"
                                    />
                                </Transition>
                            </div>
                        </TextTooltip>
                    </div>
                </TransitionGroup>
            </div>
        </div>

        <Transition
            enter-active-class="duration-300 ease-out"
            enter-from-class="transform opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
        >
            <PopoverPanel
                class="absolute z-10 transition-opacity"
                ref="floating"
                :style="floatingStyles"
            >
                <div
                    class="flex gap-x-1.5 bg-white px-3 py-2 rounded-full border border-primary-border"
                >
                    <PopoverButton
                        v-for="emotion in availableEmotionsWithUserInteraction"
                        :key="emotion.id"
                        @click="emote(emotion.id)"
                    >
                        <component
                            :is="emotionIcons[emotion.id]"
                            :class="[
                                emotion.user_interacted
                                    ? `bg-${rootModelClass}-accent text-${rootModelClass} rounded-full`
                                    : '',
                                emotion.user_interacted
                                    ? 'hover:scale-125'
                                    : 'hover:scale-150',
                                'w-10 h-10  hover:-rotate-12 transform transition duration-200 p-2 rounded-2xl',
                            ]"
                            :weight="
                                emotion.user_interacted ? 'fill' : 'regular'
                            "
                        />
                    </PopoverButton>
                </div>
            </PopoverPanel>
        </Transition>
    </Popover>
</template>

<script>
import { ref } from 'vue'
import {
    useFloating,
    autoUpdate,
    autoPlacement,
    shift,
    offset,
} from '@floating-ui/vue'

import {
    PhSmiley,
    PhThumbsUp,
    PhLightbulb,
    PhGraduationCap,
    PhConfetti,
    PhHeart,
} from '@phosphor-icons/vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'

export default {
    name: 'Emotions',

    components: {
        TextTooltip,
        Popover,
        PopoverButton,
        PopoverPanel,
        PhSmiley,
        PhThumbsUp,
        PhLightbulb,
        PhGraduationCap,
        PhConfetti,
        PhHeart,
    },

    props: {
        availableEmotions: {
            type: Array,
            default: () => [],
        },
        initialEmotions: {
            type: Array,
            required: true,
        },
        modelId: {
            type: Number,
            required: true,
        },
        modelClass: {
            type: String,
            required: true,
        },
        rootModelClass: {
            type: String,
            required: true,
        },
        rootModelId: {
            type: Number,
            required: true,
        },
        isCommentEmotions: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        echoChannel: null,
        emotions: [],
        isOpen: false,
        hoverTimeout: null,
    }),

    mounted() {
        this.initEmotions(this.initialEmotions)
        this.initEcho()
    },

    beforeUnmount() {
        this.destroyEcho()
    },

    computed: {
        /**
         * Returns the available emotions with a boolean indicating if
         * the user has emoted usin it
         */
        availableEmotionsWithUserInteraction() {
            return this.availableEmotions.map((emotion) => ({
                ...emotion,
                user_interacted:
                    this.emotions.find((e) => e.id == emotion.id)
                        ?.user_interacted || false,
            }))
        },

        emotionIcons() {
            return {
                positive: PhThumbsUp,
                insightful: PhLightbulb,
                educational: PhGraduationCap,
                celebrative: PhConfetti,
                loving: PhHeart,
            }
        },

        activeEmotions() {
            return this.emotions.filter((emotion) => emotion.count > 0)
        },
    },

    methods: {
        emote(emotion) {
            axios
                .post(route('api.interaction.emote'), {
                    model_id: this.modelId,
                    model_class: this.modelClass,
                    emotion_id: emotion,
                })
                .then(({ data }) => {
                    this.initEmotions(data)
                })
        },

        /**
         * Initialize the emotions
         * Iterate through the available emotions and add count, previousCount, user_interacted and users from the
         * emotions parameter if a value from that paremter matches the availableEmotion id
         * Add previousCount so that we know what the count was before the update
         * @param emotions
         */
        initEmotions(emotions) {
            this.emotions = this.availableEmotions.map((emotion) => {
                let emotionValue = emotions.find(
                    (emotionValue) => emotionValue.emotion_id == emotion.id
                )
                let oldEmotion = this.emotions.find((e) => e.id == emotion.id)
                return {
                    id: emotion.id,
                    order: emotion.order,
                    count: emotionValue?.count || 0,
                    previousCount: oldEmotion
                        ? oldEmotion.count
                        : emotionValue?.count || 0,
                    user_interacted: emotionValue?.user_interacted || false,
                    users: emotionValue?.users || '',
                }
            })
        },

        initEcho() {
            if (this.$page.props.auth.user) {
                this.echoChannel = window.Echo.private(
                    `${this.rootModelClass}-${this.rootModelId}-interactions`
                )

                this.echoChannel.listen('EmotionEvent', this.handleEmotionEvent)
            }
        },

        destroyEcho() {
            if (this.$page.props.auth.user) {
                window.Echo.leaveChannel(
                    `${this.rootModelClass}-${this.rootModelId}-interactions`
                )
                this.echoChannel = null
            }
        },

        /**
         * Check if the event is for the current model
         * The channel is used for both the emotions for the model and the emotions for its comments

         */
        handleEmotionEvent(event) {
            if (
                event.modelClass == this.modelClass &&
                event.modelId == this.modelId
            ) {
                this.fetchEmotions()
            }
        },

        fetchEmotions() {
            axios
                .get(
                    route('api.interaction.emotions', {
                        modelId: this.modelId,
                        modelClass: this.modelClass,
                        interactionId: this.interactionId,
                    })
                )
                .then(({ data }) => {
                    this.initEmotions(data)
                })
        },

        getActiveEmotionTooltipText(emotion) {
            let str = emotion.users

            let nbrUsers = emotion.users.split(',').length
            let nbrOthers = emotion.count - nbrUsers

            if (nbrOthers == 1) {
                str += ' '
                str += this.$lang.get('and :count other', {
                    count: this.$lang.get('one'),
                })
                str += '...'
            } else if (nbrOthers > 1) {
                str += ' '
                str += this.$lang.get('and :count others', {
                    count: nbrOthers,
                })
                str += '...'
            }

            return str
        },
    },

    setup() {
        const reference = ref(null)
        const floating = ref(null)

        const { floatingStyles, placement, middlewareData } = useFloating(
            reference,
            floating,
            {
                middleware: [
                    autoPlacement({
                        allowedPlacements: ['top-start', 'bottom-start'],
                    }),
                    shift(),
                    offset(5),
                ],
                whileElementsMounted: autoUpdate,
            }
        )

        return {
            placement,
            reference,
            floating,
            floatingStyles,
            middlewareData,
        }
    },
}
</script>
